import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Eye, EyeOff } from "@/shared/icons";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { toastApiError } from "@/api/client";
import { useAuthStore } from "@/modules/auth/store";
import { useChangePassword, useMe, meQueryKey } from "@/api/hooks/auth";
import { useUpdateClientCommunicationEmail } from "@/api/hooks/clientportal";
import { useConfirm } from "@/shared/components/ConfirmDialog";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Mandatory post-login gate for client users. Fires when the server reports
 * `needsPasswordChange` (first-login credential — the backend blocks every
 * portal endpoint until it is replaced) and/or `needsEmailConfirmation`
 * (communication email never confirmed). Blocking — no dismiss — until both
 * are cleared. Renders nothing for internal roles.
 *
 * Password change bumps auth_version server-side, revoking every session —
 * the user is warned via ConfirmDialog before submitting, then cleanly signed
 * out to log back in with the new password. (A prior transparent re-login
 * raced the `auth.force_logout` WebSocket push and lost, dumping the user on
 * the login page with no explanation.)
 */
export function ClientEmailGate() {
  const role = useAuthStore((s) => s.user?.role);
  const forceLogout = useAuthStore((s) => s.forceLogout);
  const setSuppressForceLogout = useAuthStore((s) => s.setSuppressForceLogout);
  const isClient = role === "client";
  const me = useMe(isClient);
  const qc = useQueryClient();
  const updateEmail = useUpdateClientCommunicationEmail();
  const changePassword = useChangePassword();
  const { confirm, node: confirmNode } = useConfirm();

  const existing = me.data?.user.communicationEmail ?? "";
  const needsEmail = isClient && me.data?.needsEmailConfirmation === true;
  const needsPassword = isClient && me.data?.needsPasswordChange === true;
  const needs = needsEmail || needsPassword;

  const [email, setEmail] = useState("");
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  // Password changed on the server; session revoked. Blocking notice until
  // the user acknowledges and is sent to the login page.
  const [pwChanged, setPwChanged] = useState(false);

  // Prefill with the existing address when the dialog opens so a confirm is
  // one click.
  useEffect(() => {
    if (needs) setEmail(existing);
  }, [needs, existing]);

  const signOutToLogin = () => {
    forceLogout("user");
    window.location.assign("/login");
  };

  if (pwChanged) {
    return (
      <Dialog open disableEscapeKeyDown maxWidth="xs" fullWidth onClose={() => {}}>
        <DialogTitle>Password changed</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "var(--ink-2)" }}>
            Your password was updated successfully. For security you have been
            signed out everywhere — sign in again with your new password.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={signOutToLogin}>
            Sign In Again
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!needs) return null;

  const trimmedEmail = email.trim();
  const emailValid = !needsEmail || EMAIL_RE.test(trimmedEmail);
  const pwValid =
    !needsPassword ||
    (currentPw.length > 0 &&
      newPw.length >= 8 &&
      newPw === confirmPw &&
      newPw !== currentPw);
  const valid = emailValid && pwValid;
  const hasExisting = existing.trim().length > 0;

  const pwError = (): string => {
    if (!touched || !needsPassword) return " ";
    if (!newPw) return " ";
    if (newPw.length < 8) return "At least 8 characters.";
    if (newPw === currentPw) return "Must differ from the current password.";
    if (confirmPw && newPw !== confirmPw) return "Passwords do not match.";
    return " ";
  };

  const pwToggle = (
    <InputAdornment position="end">
      <IconButton
        size="small"
        edge="end"
        aria-label={showPw ? "Hide passwords" : "Show passwords"}
        title={showPw ? "Hide passwords" : "Show passwords"}
        onClick={() => setShowPw((v) => !v)}
      >
        {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
      </IconButton>
    </InputAdornment>
  );

  const submit = async () => {
    setTouched(true);
    if (!valid || saving) return;
    if (needsPassword) {
      const ok = await confirm({
        title: "Change password?",
        message:
          "For security, you will be signed out of the portal and must log in again with your new password.",
        confirmLabel: "Change Password",
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      // Email first — this endpoint is exempt from the server password gate
      // and (for phone-only clients) also becomes the login email.
      if (needsEmail) {
        await updateEmail.mutateAsync(trimmedEmail);
      }
      if (needsPassword) {
        // The server bumps auth_version the moment the change commits and
        // pushes auth.force_logout at this session — often before the HTTP
        // response returns. Suppress the listener/interceptor auto-logout
        // BEFORE the call so the user sees our sign-out notice instead of
        // being dumped on the login page mid-flight.
        setSuppressForceLogout(true);
        try {
          await changePassword.mutateAsync({
            currentPassword: currentPw,
            newPassword: newPw,
          });
        } catch (e) {
          setSuppressForceLogout(false);
          throw e;
        }
        // Stop background refetches — every token is revoked; they'd only
        // produce 401 noise behind the notice.
        await qc.cancelQueries();
        setPwChanged(true);
        return;
      }
      await qc.invalidateQueries({ queryKey: meQueryKey });
      toast.success("Email confirmed");
    } catch (e) {
      toastApiError(e, "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open disableEscapeKeyDown maxWidth="xs" fullWidth onClose={() => {}}>
      <DialogTitle>
        {needsPassword ? "Secure your account" : hasExisting ? "Confirm your email" : "Add your email"}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: "var(--ink-2)" }}>
          {needsPassword
            ? "Set a new password" +
              (needsEmail ? " and your email address" : "") +
              " to continue."
            : hasExisting
              ? "We use this address to send you document acknowledgements and updates. Confirm it is correct, or update it below."
              : "Please add an email address. We use it to send you document acknowledgements and updates."}
        </Typography>
        {needsEmail && (
          <TextField
            autoFocus
            fullWidth
            size="small"
            type="email"
            label="Email address"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched(true)}
            error={touched && !emailValid}
            helperText={touched && !emailValid ? "Enter a valid email address." : " "}
            sx={{ mb: needsPassword ? 1 : 0 }}
          />
        )}
        {needsPassword && (
          <>
            <TextField
              autoFocus={!needsEmail}
              fullWidth
              size="small"
              type={showPw ? "text" : "password"}
              label="Current Password / Phone"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              sx={{ mb: 1.5 }}
              helperText="First login? Use your 10-digit registered phone number."
              InputProps={{ endAdornment: pwToggle }}
            />
            <TextField
              fullWidth
              size="small"
              type={showPw ? "text" : "password"}
              label="New password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              onBlur={() => setTouched(true)}
              error={touched && newPw.length > 0 && (newPw.length < 8 || newPw === currentPw)}
              helperText={pwError()}
              sx={{ mb: 1.5 }}
              InputProps={{ endAdornment: pwToggle }}
            />
            <TextField
              fullWidth
              size="small"
              type={showPw ? "text" : "password"}
              label="Confirm new password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              onBlur={() => setTouched(true)}
              error={touched && confirmPw.length > 0 && newPw !== confirmPw}
              helperText={touched && confirmPw.length > 0 && newPw !== confirmPw ? "Passwords do not match." : " "}
              InputProps={{ endAdornment: pwToggle }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={submit} disabled={saving || !valid}>
          {saving ? "Saving..." : needsPassword ? "Set Password" : hasExisting ? "Confirm" : "Save"}
        </Button>
      </DialogActions>
      {confirmNode}
    </Dialog>
  );
}
